/**
 * Top Paths: journeys that hit every anchor column in order (not only first → last by name).
 *
 * How it works:
 *   1. Scope nodes/links to global steps from the first anchor through the last anchor column
 *      (from API `anchors[].step` ordering).
 *   2. Queue walk from the first anchor: advance milestones with strict anchor-column matching;
 *      if no path is found (links often skip primary cells), retry with relaxed ordered event names.
 *   3. Record when all anchors (0…n−1) have been hit in order; dedupe by event sequence;
 *      sort by volume descending.
 */

function fmt(name) {
  if (!name) return "—";
  const s = String(name);
  return s.length > 24 ? `${s.slice(0, 23)}…` : s;
}

function isUsableNode(n) {
  if (!n?.id) return false;
  if (n.isOthers || n.isThrough || n.isDropoff) return false;
  const ev = String(n.eventName || "");
  return ev !== "Others" && ev !== "Through" && ev !== "Drop-off";
}

/**
 * @param {object[]} nodes   – flow API response nodes
 * @param {object[]} links   – flow API response links
 * @param {object[]} anchors – flow API response anchors (include step, stepsAfter, eventName)
 * @param {object}  [opts] maxPaths, maxDepth, maxCollect, maxBranch, maxTraversalPops
 * @returns {{ id, events, counts, endVolume }[]}  sorted by endVolume desc
 */
export function computeTopPathsFromFlow(nodes, links, anchors, opts = {}) {
  if (!nodes?.length || !anchors?.length) return [];

  const sorted = [...anchors].sort((a, b) => (a.step ?? 0) - (b.step ?? 0));
  if (sorted.length < 2) return [];

  const firstAnchor = sorted[0];
  const lastAnchor = sorted[sorted.length - 1];
  const firstStep = firstAnchor.step ?? 0;
  const lastStep = lastAnchor.step ?? firstStep;
  const maxStep = lastStep;
  const nMilestones = sorted.length;

  /** Strict: anchor event at that anchor’s global column (primary cell). */
  function matchesNextMilestoneStrict(node, k) {
    if (k < 0 || k >= nMilestones || !node) return false;
    const want = String(sorted[k].eventName || "").trim();
    if (String(node.eventName || "").trim() !== want) return false;
    const col = sorted[k].step;
    const ns = node.step != null ? Number(node.step) : NaN;
    if (col != null && !Number.isNaN(ns) && ns === Number(col)) return true;
    if (node.isAnchor === true && Number(node.anchorIndex) === k) return true;
    if (col != null && typeof node.id === "string") {
      const prefix = `step_${col}__`;
      if (node.id.startsWith(prefix)) return true;
    }
    return false;
  }

  /** Relaxed: ordered event-name subsequence (matches journeys when links skip anchor primary cells). */
  function matchesNextMilestoneRelaxed(node, k) {
    if (k < 0 || k >= nMilestones || !node) return false;
    return (
      String(node.eventName || "").trim() === String(sorted[k].eventName || "").trim()
    );
  }

  const nodeById = new Map(nodes.map(n => [n.id, n]));

  // ── scope ──────────────────────────────────────────────────────────────────
  const scopeIds = new Set(
    nodes
      .filter(n => isUsableNode(n) && n.step >= firstStep && n.step <= maxStep)
      .map(n => n.id)
  );

  // ── forward adjacency (sorted by link value desc) ──────────────────────────
  const out = new Map();
  for (const l of links) {
    if (!scopeIds.has(l.source) || !scopeIds.has(l.target)) continue;
    const s = nodeById.get(l.source);
    const t = nodeById.get(l.target);
    if (!s || !t || t.step <= s.step) continue;
    if (!out.has(l.source)) out.set(l.source, []);
    out.get(l.source).push(l);
  }
  for (const arr of out.values()) arr.sort((a, b) => (b.value || 0) - (a.value || 0));

  // ── start node (first anchor column; tolerate missing flags after merges) ───
  const firstEv = String(firstAnchor.eventName || "").trim();
  const startNode =
    nodes.find(
      (n) =>
        n.isAnchor &&
        Number(n.step) === Number(firstStep) &&
        Number(n.anchorIndex) === 0
    ) ??
    nodes.find((n) => n.isAnchor && Number(n.step) === Number(firstStep)) ??
    nodes.find(
      (n) =>
        isUsableNode(n) &&
        Number(n.step) === Number(firstStep) &&
        String(n.eventName || "").trim() === firstEv
    );
  if (!startNode) return [];

  // Queue enumeration (no per-state dedupe): `seen` on (nodeId, nextMilestone) was collapsing all
  // routes that reconverge, yielding only a handful of completions. Step strictly increases on
  // links, so the graph is acyclic along a walk; cap work with maxTraversalPops / maxCollect.
  const {
    maxPaths = 100,
    maxDepth = 80,
    maxCollect = 4000,
    maxBranch = Infinity,
    maxTraversalPops = 2_000_000,
  } = opts;

  function chainMinVolume(chain) {
    let vol = startNode.count || 0;
    for (const l of chain) {
      const t = nodeById.get(l.target);
      if (!t) break;
      vol = Math.min(vol, l.value || 0, t.count || 0);
    }
    return vol;
  }

  function collectCompleted(matchesFn) {
    const done = [];
    const queue = [];

    function expandFromNode(nodeId, nextMilestone, chain) {
      if (done.length >= maxCollect) return;
      const node = nodeById.get(nodeId);
      if (!node) return;

      let k = nextMilestone;
      if (k < nMilestones && matchesFn(node, k)) k += 1;

      if (k === nMilestones) {
        if (chain.length > 0) done.push(chain);
        return;
      }

      if (chain.length >= maxDepth) return;

      const rawOut = out.get(nodeId) || [];
      const outs =
        maxBranch === Infinity || maxBranch >= rawOut.length
          ? rawOut
          : rawOut.slice(0, maxBranch);

      for (const l of outs) {
        const t = nodeById.get(l.target);
        if (!t) continue;
        const nextChain = chain.concat([l]);
        if (nextChain.length > maxDepth) continue;
        queue.push({ nodeId: l.target, nextMilestone: k, chain: nextChain });
      }
    }

    expandFromNode(startNode.id, 0, []);

    let head = 0;
    let pops = 0;
    while (
      head < queue.length &&
      done.length < maxCollect &&
      pops < maxTraversalPops
    ) {
      const { nodeId, nextMilestone, chain } = queue[head++];
      pops += 1;
      expandFromNode(nodeId, nextMilestone, chain);
    }

    return done;
  }

  let pathMatchMode = "strict";
  let completed = collectCompleted(matchesNextMilestoneStrict);
  if (completed.length === 0 && nMilestones > 1) {
    pathMatchMode = "relaxed";
    completed = collectCompleted(matchesNextMilestoneRelaxed);
  }

  completed.sort((a, b) => chainMinVolume(b) - chainMinVolume(a));

  const matchForPath = pathMatchMode === "strict" ? matchesNextMilestoneStrict : matchesNextMilestoneRelaxed;

  // ── build path records ─────────────────────────────────────────────────────
  // Deduplicate by ordered event-name sequence; first occurrence wins (highest volume
  // because DFS visits higher-value edges first).
  const byKey = new Map();

  let postMilestoneReject = 0;

  for (const chain of completed) {
    // Reconstruct node sequence
    const nodePath = [startNode];
    let broken = false;
    for (const l of chain) {
      const n = nodeById.get(l.target);
      if (!n) { broken = true; break; }
      nodePath.push(n);
    }
    if (broken || nodePath.length < 2) continue;

    let mk = 0;
    for (const n of nodePath) {
      if (mk < nMilestones && matchForPath(n, mk)) mk += 1;
    }
    if (mk !== nMilestones) {
      postMilestoneReject += 1;
      continue;
    }

    const key = nodePath.map(n => String(n.eventName || "")).join("\x01");
    if (byKey.has(key)) continue; // keep first after completed sorted by volume

    // Counts: flow = min(link.value, target.count, prev) along the chain
    let vol = startNode.count || 0;
    const counts = [vol];
    for (const l of chain) {
      const t = nodeById.get(l.target);
      if (!t) break;
      vol = Math.min(vol, l.value || 0, t.count || 0);
      counts.push(vol);
    }

    byKey.set(key, {
      id: chain.map(l => `${l.source}>${l.target}`).join("|") || key,
      events: nodePath.map(n => String(n.eventName || "")),
      labels: nodePath.map(n => fmt(n.eventName)),
      counts,
      endVolume: counts[counts.length - 1] ?? 0,
    });
  }

  const result = [...byKey.values()]
    .sort((a, b) => (b.endVolume || 0) - (a.endVolume || 0))
    .slice(0, maxPaths);

  return result;
}
